import { renderRichText, applyLocaleToUrl } from "@/utils/formatters";
import React from "react";
import PropTypes from "prop-types";
import { useRouter } from "next/router";
import { getLinkTargetTab } from "@/utils/helper";

const ContactUs = ({ data }) => {
  const { locale } = useRouter();

  const {
    title,
    description,
    officeDetails,
    resourceTitle: resourcesTitle,
    resourcesLinks,
    ctaButtons,
    officeTitle,
  } = data || {};

  const offices = officeDetails?.[0]?.fields?.offices || [];

  return (
    <section className="max-w-[980px] mx-auto px-4 pb-8">
      {/* Title */}
      <div className="max-w-6xl mx-auto text-center">
        <h1 className="text-[45px] py-10 md:text-[60px] font-[HelveticaLight, Helvetica, sans-serif] text-[#232323]">
          {title}
        </h1>
        <div className="border-t border-black-300"></div>
      </div>

      {/* Layout */}
      <div className="max-w-6xl mx-auto mt-8 md:mt-[37px] grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
        {/* LEFT */}
        <div className="md:col-span-8">
          <div className="!font-[HelveticaRoman,_Helvetica,_sans-serif] leading-[1.428571429] text-[14px] text-[#232323] ">
            {description && renderRichText(description, locale)}
          </div>

          <div className="mt-5 text-sm text-gray-800">
            <h3 className="text-[24px] font-[HelveticaRoman,_Helvetica,_sans-serif]">
              {officeTitle}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 font-[HelveticaRoman,_Helvetica,_sans-serif]">
              {offices.map((office, oIndex) => {
                const officeFields = office?.fields || {};
                const contactLabels = officeFields?.contactLabel || [];
                const cta = officeFields?.ctaButton?.[0]?.fields;

                return (
                  <div
                    key={office?.sys?.id || oIndex}
                    className={`border-t border-gray-300 mt-[30px] pt-[30px] min-h-[inherit] md:min-h-[275px]`}
                  >
                    <h4 className="text-sm mb-2 !font-bold font-[helveticaBold,_Helvetica,_sans-serif]">
                      {officeFields?.title}
                    </h4>

                    {officeFields?.details && (
                      <div className="text-sm pr-[30px]">
                        {renderRichText(officeFields.details, locale)}
                      </div>
                    )}

                    <div className="text-sm font-[HelveticaRoman,_Helvetica,_sans-serif]">
                      {contactLabels.map((item, i) => {
                        const itemFields = item?.fields || {};
                        const href = itemFields?.href?.trim();
                        const nohrefLinks = href == null || href === "#";

                        if (itemFields.description) {
                          return (
                            <p
                              key={item?.sys?.id || i}
                              className="font-[HelveticaRoman, Helvetica, sans-serif] pr-[30px] pb-[10px]"
                            >
                              {renderRichText(itemFields.description, locale)}
                            </p>
                          );
                        } else {
                          return (
                            <>
                              {itemFields?.exceptionalText && (
                                <span>{itemFields.exceptionalText}</span>
                              )}
                              <p key={item?.sys?.id || i}>
                                {" "}
                                <b className="!font-bold">
                                  {itemFields?.label}:{" "}
                                </b>
                                {nohrefLinks ? (
                                  <span
                                    className={
                                      href === "#"
                                        ? "text-[#337ab7]"
                                        : "text-[#232323]"
                                    }
                                  >
                                    {itemFields?.value}
                                  </span>
                                ) : (
                                  <a
                                    href={itemFields?.href}
                                    className={`no-underline text-[#337ab7] hover:!no-underline !font-[HelveticaRoman,Helvetica,sans-serif]`}
                                  >
                                    {itemFields?.value}
                                  </a>
                                )}
                              </p>
                            </>
                          );
                        }
                      })}
                    </div>
                    {cta && (
                      <a
                        href={cta?.url}
                        className="inline-block bg-[#337ab7] text-white text-[0.8em] px-[10px] py-[5px] text-center uppercase no-underline hover:!no-underline !text-white rounded-[3px] shadow-[0_2px_2px_rgba(0,0,0,0.2)] transition-colors hover:bg-[#000] hover:text-[#ccc] mt-[5px]"
                      >
                        {cta?.label}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="flex flex-col md:col-span-4">
          <div className="block md:hidden my-6 border-t border-gray-300"></div>

          <h3 className="text-base md:text-[24px] leading-[1.1] font-[500] font-[HelveticaRoman,_Helvetica,_sans-serif] text-gray-800 uppercase">
            {resourcesTitle}
          </h3>
          <ul className="mt-3 text-sm list-disc font-[HelveticaRoman,_Helvetica,_sans-serif]">
            {resourcesLinks?.map((link, index) => {
              const linkFields = link?.fields || {};
              return (
                <li key={link?.sys?.id || index} className="ml-12">
                  <a
                    href={applyLocaleToUrl(linkFields?.href, locale)}
                    target={getLinkTargetTab(link?.fields?.openerType)}
                    className="text-blue-800 hover:!no-underline text-[14px] color-[#337ab7] !font-[HelveticaRoman,_Helvetica,_sans-serif]"
                  >
                    {linkFields?.label}
                  </a>
                </li>
              );
            })}
          </ul>
          <div className="space-y-4">
            {ctaButtons?.map((btn, index) => {
              const btnFields = btn?.fields || {};
              return (
                <a
                  key={btn?.sys?.id || index}
                  href={applyLocaleToUrl(btnFields?.href, locale)}
                  className="block w-full text-center md:max-w-[270px] max-h-[41px] bg-[#364573] text-white pb-3 pt-2 rounded mt-[60px] no-underline !text-white hover:!no-underline !font-[HelveticaRoman,_Helvetica,_sans-serif]"
                >
                  {btnFields?.label}
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const SysType = PropTypes.shape({
  id: PropTypes.string,
});

const CTAType = PropTypes.shape({
  sys: SysType,
  fields: PropTypes.shape({
    label: PropTypes.string,
    url: PropTypes.string,
    href: PropTypes.string,
  }),
});

const ContactLabelType = PropTypes.shape({
  sys: SysType,
  fields: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    href: PropTypes.string,
  }),
});

const OfficeType = PropTypes.shape({
  sys: SysType,
  fields: PropTypes.shape({
    title: PropTypes.string,
    details: PropTypes.any,
    contactLabel: PropTypes.arrayOf(ContactLabelType),
    ctaButton: PropTypes.arrayOf(CTAType),
  }),
});

ContactUs.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    officeTitle: PropTypes.string,
    resourceTitle: PropTypes.string,
    officeDetails: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          offices: PropTypes.arrayOf(OfficeType),
        }),
      })
    ),
    resourcesLinks: PropTypes.arrayOf(
      PropTypes.shape({
        sys: SysType,
        fields: PropTypes.shape({
          label: PropTypes.string,
          href: PropTypes.string,
        }),
      })
    ),
    ctaButtons: PropTypes.arrayOf(CTAType),
  }),
};

export default ContactUs;
